import useCurrentUser from 'src/admin/users/hooks/useCurrentUser';
import { useClients } from 'src/clients/hooks/useClients';
import { useFamily } from 'src/clients/hooks/useFamily';
import { useFamilyRoles } from 'src/clients/hooks/useFamilyRoles';
import { familyRoleLabel, getFamilyMemberClientIds, isFamilyMember } from 'src/clients/utils';
import type { AddParticipantBody } from 'src/meetings/hooks/useMeetingParticipants';
import type { FamilyMeeting } from 'src/meetings/hooks/useScopedMeetings';
import type { MeetingScope } from 'src/meetings/scope';
import { getClientParticipantIds } from 'src/meetings/utils/event-utils';
import { useMeetingFamilyId } from './useMeetingFamilyId';
import {
  connectionNameKey,
  type ProfessionalConnection,
  useProfessionalConnections,
} from './useProfessionalConnections';

export type AttendeeGroup = 'adviser' | 'client' | 'guest' | 'keyContact';

export type Attendee = {
  id: string;
  name: string;
  role: string;
  group: AttendeeGroup;
  participantId: string | null;
  defaultChecked: boolean;
  addBody: AddParticipantBody;
  isExternalGuest: boolean;
};

/**
 * Builds the attendee list shown in the meeting member-selection modals:
 * the advisor, every member of the meeting's family, plus anyone already on the
 * meeting who isn't one of those. For an individual scope the family is resolved
 * from the client so the same roster is offered as in the family view; a client
 * with no family simply surfaces via the existing-participant pass.
 * `defaultChecked` reflects who is currently on the meeting.
 *
 * Key contacts already on the meeting are recognised against the professional
 * connections of every client on the roster; until `isLoading` clears one can
 * still be classified as a guest. `offeredConnections` are the ones to offer as
 * new key contacts: in the client view only that client's own, since the adviser
 * is meeting them rather than the household.
 */
export function useMeetingAttendees(
  scope: MeetingScope,
  meeting: FamilyMeeting,
): { attendees: Attendee[]; offeredConnections: ProfessionalConnection[]; isLoading: boolean } {
  const { familyId, isLoading: isFamilyIdLoading } = useMeetingFamilyId(scope, meeting);
  const { family, isLoading: isFamilyLoading } = useFamily(familyId);
  const { clients, isLoading: isClientsLoading } = useClients(
    { familyId },
    { enabled: !!familyId },
  );
  const { roleNameById } = useFamilyRoles();
  const { currentUser } = useCurrentUser();
  const { connections, isLoading: isConnectionsLoading } = useProfessionalConnections(
    rosterClientIds(scope, family, meeting),
  );

  const connectionNames = new Set(
    connections.map(connection => connectionNameKey(connection.name)),
  );

  return {
    attendees: buildAttendees({
      family,
      clients,
      roleNameById,
      currentUser,
      meeting,
      connectionNames,
    }),
    offeredConnections:
      scope.kind === 'individual'
        ? connections.filter(connection => connection.clientId === scope.clientId)
        : connections,
    isLoading: isFamilyIdLoading || isFamilyLoading || isClientsLoading || isConnectionsLoading,
  };
}

type Family = NonNullable<ReturnType<typeof useFamily>['family']>;
type Client = ReturnType<typeof useClients>['clients'][number];
type CurrentUser = ReturnType<typeof useCurrentUser>['currentUser'];
type MeetingParticipant = FamilyMeeting['eventParticipants'][number]['participant'];

function rosterClientIds(
  scope: MeetingScope,
  family: Family | null,
  meeting: FamilyMeeting,
): string[] {
  return [
    ...new Set([
      ...(scope.kind === 'individual' ? [scope.clientId] : []),
      ...(family ? getFamilyMemberClientIds(family) : []),
      ...getClientParticipantIds(meeting),
    ]),
  ];
}

function buildAttendees({
  family,
  clients,
  roleNameById,
  currentUser,
  meeting,
  connectionNames,
}: {
  family: Family | null;
  clients: Client[];
  roleNameById: Record<string, string>;
  currentUser: CurrentUser;
  meeting: FamilyMeeting;
  connectionNames: Set<string>;
}): Attendee[] {
  const clientById = Object.fromEntries(clients.map(client => [client.id, client]));

  const participantByKey = new Map<
    string,
    { participantId: string | null; participant: MeetingParticipant }
  >();
  for (const eventParticipant of meeting.eventParticipants) {
    const participant = eventParticipant.participant;
    participantByKey.set(participantKey(participant), {
      participantId: participant.id,
      participant,
    });
  }

  const attendees: Attendee[] = [];
  const representedKeys = new Set<string>();

  if (currentUser) {
    const key = `user:${currentUser.id}`;
    representedKeys.add(key);
    attendees.push({
      id: key,
      name: `${currentUser.firstName ?? ''} (You)`.trim(),
      role: 'Adviser',
      group: 'adviser',
      participantId: participantByKey.get(key)?.participantId ?? null,
      defaultChecked: participantByKey.has(key),
      addBody: { participantType: 'USER', userId: currentUser.id },
      isExternalGuest: false,
    });
  }

  for (const clientId of family ? getFamilyMemberClientIds(family) : []) {
    const client = clientById[clientId];
    if (!client) continue;
    const key = `client:${clientId}`;
    representedKeys.add(key);
    attendees.push({
      id: key,
      name: `${client.firstName} ${client.lastName}`.trim(),
      role: familyRoleLabel(family, roleNameById, clientId) ?? 'Family member',
      group: 'client',
      participantId: participantByKey.get(key)?.participantId ?? null,
      defaultChecked: participantByKey.has(key),
      addBody: { participantType: 'CLIENT', clientId },
      isExternalGuest: false,
    });
  }

  // Anyone already on the meeting who isn't the advisor or a scoped client —
  // e.g. a client added via "Choose a client", or an external attendee.
  for (const [key, { participantId, participant }] of participantByKey) {
    if (representedKeys.has(key)) continue;
    attendees.push({
      id: key,
      participantId,
      defaultChecked: true,
      ...extraAttendeeFields(participant, family, roleNameById, connectionNames),
    });
  }

  return attendees;
}

// A client already on the meeting: labelled by their family standing when they belong to
// this family, and simply as a client when they don't.
function clientRole(
  family: Family | null,
  roleNameById: Record<string, string>,
  clientId: string,
): string {
  const label = familyRoleLabel(family, roleNameById, clientId);
  if (label) return label;
  return isFamilyMember(family, clientId) ? 'Family member' : 'Client';
}

function participantKey(participant: MeetingParticipant): string {
  if (participant.client?.id) return `client:${participant.client.id}`;
  if (participant.user?.id) return `user:${participant.user.id}`;
  return `participant:${participant.id ?? ''}`;
}

function extraAttendeeFields(
  participant: MeetingParticipant,
  family: Family | null,
  roleNameById: Record<string, string>,
  connectionNames: Set<string>,
): {
  name: string;
  role: string;
  group: AttendeeGroup;
  addBody: AddParticipantBody;
  isExternalGuest: boolean;
} {
  if (participant.client?.id) {
    return {
      name: `${participant.client.firstName} ${participant.client.lastName}`.trim(),
      role: clientRole(family, roleNameById, participant.client.id),
      group: 'client',
      addBody: { participantType: 'CLIENT', clientId: participant.client.id },
      isExternalGuest: false,
    };
  }
  if (participant.user?.id) {
    return {
      name: `${participant.user.firstName ?? ''} ${participant.user.lastName ?? ''}`.trim(),
      role: 'Adviser',
      group: 'adviser',
      addBody: { participantType: 'USER', userId: participant.user.id },
      isExternalGuest: false,
    };
  }
  const name = participant.name ?? 'Guest';
  const isKeyContact = !!participant.name && connectionNames.has(connectionNameKey(name));
  return {
    name,
    role: participant.description ?? 'Guest',
    group: isKeyContact ? 'keyContact' : 'guest',
    addBody: {
      participantType: 'EXTERNAL',
      name: participant.name ?? '',
      description: participant.description ?? '',
    },
    isExternalGuest: !isKeyContact,
  };
}
